import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import {
  IInspectionOptions,
  IPermitTypeField,
  IWorkflowStep,
  WorkflowDurationType,
  WorkflowStepType,
} from '~/client/graph'
import IFormFilterFieldOption from '~/client/src/desktop/interfaces/IFormFilterFieldOption'
import {
  carryServerDenials,
  denyOnRestrictedFields,
} from '~/client/src/shared/components/ResourceControls/resourceFieldAllowanceWrites'
import { ITabObject } from '~/client/src/shared/components/Tabs/Tabs'
import { getWorkflowStepLevel } from '~/client/src/shared/constants/formStatusesTags'
import {
  locationPermitFieldTypes,
  materialResourcePermitFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import { getWorkflowStepDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import OperationRulesStore from '~/client/src/shared/stores/domain/OperationRules.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import { IWorkflowTypeRuleChanges } from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { toast } from '~/client/src/shared/utils/toast'

import BicRulesDraft from './BicRulesDraft'
import { IFormTypeEnabledResources } from './FormTypeEnabledResourcesContext'
import FormTypeStateHistory from './FormTypeStateHistory'
import { updateWorkflowActions } from './utils/workflowActionValidators'

const forbiddenLastSteps = [
  WorkflowStepType.RecurringInspection,
  WorkflowStepType.Start,
]

enum FormTabSection {
  GENERAL = 'General',
  STEPS = 'Steps',
  FORMS = 'Forms',
  VIEWS = 'Views',
  CONFIRMATION = 'Confirmation',
  QR_CODES = 'QR Codes',
}

export type FormGeneralFields = Partial<
  Pick<
    WorkflowType,
    | 'name'
    | 'isEnabled'
    | 'shouldBlockOnNonWorkTimes'
    | 'isWorkflowTitleMandatory'
    | 'bookingDeadlineOptions'
    | 'autoEndStepIds'
    | 'categoryId'
    | 'maxDurationOptions'
    | 'defaultDurationOptions'
    | 'recurringOptions'
    | 'iconName'
  >
>

export default class FormTypeConfiguratorStore implements IFormTypeEnabledResources {
  @observable public formType: WorkflowType = null
  @observable public selectedWorkflowStepId: string = null
  @observable public activeFormTab: string = FormTabSection.GENERAL
  @observable public isUnsavedChangesDialogOpen: boolean = false
  @observable public handleUnsavedDialogContinue: () => void = null

  public readonly bicRulesDraft: BicRulesDraft

  @observable private formStateHistory: FormTypeStateHistory = null
  // The last server version the draft was read from or caught up with.
  private serverBase: WorkflowType = null
  private isSaving = false

  public constructor(
    formType: WorkflowType,
    private readonly formCategoriesStore: WorkflowCategoriesStore,
    private readonly saveFormType: (
      updatedFormType: WorkflowType,
      onError: () => void,
      onSaved: () => void,
      ruleChanges: IWorkflowTypeRuleChanges,
    ) => void,
    private readonly onTemplateUpdate: (template: WorkflowType) => void,
    operationRulesStore: OperationRulesStore,
  ) {
    this.bicRulesDraft = new BicRulesDraft(operationRulesStore)
    this.init(formType)
    makeObservable(this)
  }

  @action.bound
  public init(formType: WorkflowType) {
    this.formType = formType.getDeepCopy()
    this.serverBase = formType.getDeepCopy()
    this.isSaving = false

    this.resetWorkflowStepIfNeed()
    this.updateWorkflowActions()

    this.bicRulesDraft.clear()
    this.formStateHistory = new FormTypeStateHistory(
      formType,
      this.setFormType,
      this.bicRulesDraft,
    )
  }

  @action.bound
  public setSelectedWorkflowStepId(selectedWorkflowStepId: string) {
    this.selectedWorkflowStepId = selectedWorkflowStepId
  }

  @action.bound
  public changeGeneralProps(generalProps: FormGeneralFields) {
    this.formType = Object.assign(this.formType.getDeepCopy(), generalProps)

    this.formStateHistory.captureState(this.formType)
    this.onTemplateUpdate(this.formType)
  }

  @action.bound
  public changeWorkflowSettings(
    workflowSteps: IWorkflowStep[],
    inspectionOptions: IInspectionOptions,
    isAutoActivationEnabled: boolean,
  ) {
    this.formType.workflowSteps = workflowSteps
    this.formType.inspectionOptions = inspectionOptions
    this.formType.isAutoActivationEnabled = isAutoActivationEnabled

    this.updateWorkflowActions()

    this.formStateHistory.captureState(this.formType)
    this.onTemplateUpdate(this.formType)
  }

  @action.bound
  private updateWorkflowActions() {
    this.formType.workflowSteps = updateWorkflowActions(
      this.formType.workflowSteps,
    )
  }

  @action.bound
  public changePermitTypeWorkflowStep(workflowStep: IWorkflowStep) {
    const index = this.formType.workflowSteps.findIndex(
      s => s.id === workflowStep.id,
    )

    if (index === -1) {
      return
    }

    this.formType.workflowSteps[index] = workflowStep

    this.updateWorkflowActions()

    this.formStateHistory.captureState(this.formType)
    this.onTemplateUpdate(this.formType)
  }

  /**
   * Gives the draft the denial the server made for resources just turned into workflow
   * resources, so saving the draft keeps it. Applied to every undo state too rather than
   * recorded as a step of its own: undo must not take back what the server already wrote.
   * The base gets it too - the server now holds it - so the refresh that write brings is not
   * read as new additions, which would re-add an entry the user removed meanwhile. Steps that
   * change are replaced, not edited in place, so the step editor holding a copy of one re-reads
   * it.
   */
  @action.bound
  public denyEnabledResources(
    resourceTypeId: string,
    recordIds: readonly string[],
  ) {
    const deny = (type: WorkflowType) => {
      type.workflowSteps = denyOnRestrictedFields(
        type.workflowSteps,
        resourceTypeId,
        recordIds,
      )
    }

    const before = this.formType.workflowSteps
    deny(this.formType)
    this.formStateHistory.applyToAll(deny)
    if (this.serverBase) deny(this.serverBase)

    if (this.formType.workflowSteps !== before) {
      this.onTemplateUpdate(this.formType)
    }
  }

  /**
   * Whether a refreshed copy of the type being edited should be merged into the draft rather
   * than replace it: the draft holds unsaved edits, and the refresh is not the answer to its
   * own save. Reinitializing would silently discard those edits.
   */
  public keepsDraftOver(refreshed: WorkflowType): boolean {
    return (
      !!refreshed?.id &&
      refreshed.id === this.formType?.id &&
      this.hasUndo &&
      !this.isSaving
    )
  }

  /**
   * Keeps the draft over a refresh of the same type, carrying in the deny-list entries the
   * server added since the draft's base - a resource created or turned into a workflow
   * resource meanwhile - so the draft's save does not drop them. Like denyEnabledResources it
   * reaches every undo state. Other server-side changes to the type are not merged; the
   * draft's save overwrites them as before.
   */
  @action.bound
  public mergeRefresh(refreshed: WorkflowType) {
    const carry = (type: WorkflowType) => {
      type.workflowSteps = carryServerDenials(
        type.workflowSteps,
        this.serverBase?.workflowSteps,
        refreshed.workflowSteps,
      )
    }

    const before = this.formType.workflowSteps
    carry(this.formType)
    this.formStateHistory.applyToAll(carry)
    this.serverBase = refreshed.getDeepCopy()

    if (this.formType.workflowSteps !== before) {
      this.onTemplateUpdate(this.formType)
    }
  }

  @action.bound
  public removeFieldFromNotifications(fieldId: string) {
    this.formType.workflowSteps.forEach((step, index) => {
      if (step.type === WorkflowStepType.RecurringInspection) {
        return
      }

      const currentIds = step.notificationFieldIds || []

      if (currentIds.includes(fieldId)) {
        this.formType.workflowSteps[index] = {
          ...step,
          notificationFieldIds: currentIds.filter(id => id !== fieldId),
        }
      }
    })
  }

  @action.bound
  public changeFieldNotificationSteps(fieldId: string, stepIds: string[]) {
    const stepIdSet = new Set(stepIds)
    let changed = false

    this.formType.workflowSteps.forEach((step, index) => {
      if (step.type === WorkflowStepType.RecurringInspection) {
        return
      }

      const currentIds = step.notificationFieldIds || []
      const hasField = currentIds.includes(fieldId)

      if (stepIdSet.has(step.id) && !hasField) {
        this.formType.workflowSteps[index] = {
          ...step,
          notificationFieldIds: [...currentIds, fieldId],
        }
        changed = true
      } else if (!stepIdSet.has(step.id) && hasField) {
        this.formType.workflowSteps[index] = {
          ...step,
          notificationFieldIds: currentIds.filter(id => id !== fieldId),
        }
        changed = true
      }
    })

    if (!changed) {
      return
    }

    this.formStateHistory.captureState(this.formType)
    this.onTemplateUpdate(this.formType)
  }

  @action.bound
  public changeActiveFormTab(newTab: FormTabSection) {
    if (this.activeFormTab === newTab) return

    if (this.hasUndo) {
      this.displayUnsavedChangesDialog(() => {
        this.handleTabChange(newTab)
      })
    } else {
      this.handleTabChange(newTab)
    }
  }

  @action
  private handleTabChange(newTab: FormTabSection) {
    this.showToastErrorIfNeed()
    this.formStateHistory.resetToInitial()

    this.activeFormTab = newTab
  }

  @action.bound
  public displayUnsavedChangesDialog(onDoneCallback: () => void) {
    this.isUnsavedChangesDialogOpen = true
    this.handleUnsavedDialogContinue = () => {
      onDoneCallback?.()
      this.formStateHistory.resetToInitial()
      this.dismissUnsavedChangesDialog()
    }
  }

  @action.bound
  public dismissUnsavedChangesDialog() {
    this.isUnsavedChangesDialogOpen = false
    this.handleUnsavedDialogContinue = null
  }

  @action.bound
  public unmountUnsavedChangesDialog() {
    this.formStateHistory.resetToInitial()
    this.dismissUnsavedChangesDialog()
  }

  // A failed save leaves the draft unsaved, so a later refresh must be merged into it again.
  private handleSaveFailed = () => {
    this.isSaving = false
  }

  @action.bound
  public saveChanges() {
    if (!this.isAllowedToSave) return

    this.updateWorkflowActions()
    this.formType.name = this.formType.name.trim()
    this.isSaving = true

    // The rule edits travel with the type and the server writes both in one transaction.
    const ruleChanges = this.bicRulesDraft.getChanges(
      this.formType.workflowSteps,
      this.serverBase?.workflowSteps,
    )

    this.saveFormType(
      this.formType,
      this.handleSaveFailed,
      ruleChanges && (() => this.bicRulesDraft.applySavedChanges(ruleChanges)),
      ruleChanges,
    )
  }

  public selectStepAndGoToForms = (stepId: string) => {
    this.setSelectedWorkflowStepId(stepId)
    this.changeActiveFormTab(FormTabSection.FORMS)
  }

  public showToastErrorIfNeed = () => {
    if (this.hasUndo) {
      toast.error(Localization.translator.workflowConfDescr.toastUnsavedMsg)
    }
  }

  public undoChangesToInitial = () => {
    this.formStateHistory.undoToInitial()
  }

  public undoChanges = () => {
    this.formStateHistory.undo()
  }

  public redoChanges = () => {
    this.formStateHistory.redo()
  }

  public get formConfiguratorTabs(): Array<ITabObject<FormTabSection>> {
    return [
      {
        title: Localization.translator.general,
        page: FormTabSection.GENERAL,
      },
      {
        title: Localization.translator.steps,
        page: FormTabSection.STEPS,
      },
      {
        title: Localization.translator.fields,
        page: FormTabSection.FORMS,
      },
      {
        title: Localization.translator.views,
        page: FormTabSection.VIEWS,
        isDisabled: true,
      },
      {
        title: Localization.translator.confirmation,
        page: FormTabSection.CONFIRMATION,
        isDisabled: true,
      },
      {
        title: Localization.translator.QRCodes,
        page: FormTabSection.QR_CODES,
      },
    ]
  }

  public get activeTabTitle(): string {
    return this.formConfiguratorTabs.find(t => t.page === this.activeFormTab)
      ?.title
  }

  public get workflowSteps(): IWorkflowStep[] {
    return this.formType.workflowSteps
  }

  public get shouldBlockOnNonWorkTimes(): boolean {
    return this.formType.shouldBlockOnNonWorkTimes
  }

  public get isGeneralTabSelected(): boolean {
    return this.activeFormTab === FormTabSection.GENERAL
  }

  public get isStepsTabSelected(): boolean {
    return this.activeFormTab === FormTabSection.STEPS
  }

  public get isFormsTabSelected(): boolean {
    return this.activeFormTab === FormTabSection.FORMS
  }

  public get isQRCodesTabSelected(): boolean {
    return this.activeFormTab === FormTabSection.QR_CODES
  }

  @computed
  public get filterFieldsMap(): Map<string, IPermitTypeField> {
    const selectedStepIndex = this.formType.workflowSteps.findIndex(
      s => s.id === this.selectedWorkflowStepId,
    )

    if (selectedStepIndex === -1) {
      return new Map<string, IPermitTypeField>()
    }

    const fieldsMap = new Map<string, IPermitTypeField>()

    for (let i = 0; i <= selectedStepIndex; i++) {
      const step = this.formType.workflowSteps[i]

      if (step.type === WorkflowStepType.RecurringInspection) {
        continue
      }

      step.fields.forEach(field => {
        fieldsMap.set(field.id, field)
      })
    }

    return fieldsMap
  }

  @computed
  public get availableFilterFieldsOptions(): IFormFilterFieldOption[] {
    const selectedStepIndex = this.formType.workflowSteps.findIndex(
      s => s.id === this.selectedWorkflowStepId,
    )

    if (selectedStepIndex === -1) {
      return []
    }

    const list = []

    for (let i = 0; i <= selectedStepIndex; i++) {
      const step = this.formType.workflowSteps[i]

      if (step.type === WorkflowStepType.RecurringInspection) {
        continue
      }

      const filterableFields = step.fields.filter(
        field =>
          locationPermitFieldTypes.includes(field.type) ||
          materialResourcePermitFieldTypes.includes(field.type),
      )

      if (
        !filterableFields.length ||
        (i === selectedStepIndex && filterableFields.length === 1)
      ) {
        continue
      }

      const stepPosition = i + 1
      const stepLevel = getWorkflowStepLevel(step.id, this.workflowSteps)
      const stepName = getWorkflowStepDisplayName(step.type, stepLevel)

      list.push({
        stepName,
        stepPosition,
      })

      filterableFields.forEach(field => {
        list.push({
          stepPosition,
          field,
          fieldPosition: step.fields.findIndex(f => f.id === field.id) + 1,
        })
      })
    }

    return list
  }

  @computed
  public get selectedWorkflowStep(): IWorkflowStep {
    return this.formType.workflowSteps.find(
      s => s.id === this.selectedWorkflowStepId,
    )
  }

  @computed
  public get isInitialStepSelected(): boolean {
    return this.selectedWorkflowStep.id === this.formType.initialStep.id
  }

  @computed
  public get hasRequestStep(): boolean {
    return this.workflowSteps.some(s => s.type === WorkflowStepType.Request)
  }

  @computed
  public get hasApprovalStep(): boolean {
    return this.workflowSteps.some(s => s.type === WorkflowStepType.Approval)
  }

  @computed
  public get warningMessage(): string {
    const { mustContainApproval, cannotBeTheLastStep } =
      Localization.translator.workflowConfDescr

    if (this.hasRequestStep && !this.hasApprovalStep) {
      return mustContainApproval
    }

    if (this.isLastStepForbidden) {
      const stepLevel = getWorkflowStepLevel(
        this.lastWorkflowStep?.id,
        this.workflowSteps,
      )
      const stepName = getWorkflowStepDisplayName(
        this.lastWorkflowStep?.type,
        stepLevel,
      )
      return cannotBeTheLastStep(stepName)
    }

    return ''
  }

  @computed
  public get isAllowedToSave(): boolean {
    return (
      !!this.formType.name?.trim() &&
      !!this.formCategoriesStore.getById(this.formType.categoryId) &&
      !this.isLastStepForbidden &&
      (!this.hasRequestStep || this.hasApprovalStep) &&
      this.isDurationValid
    )
  }

  @computed
  public get isDurationValid(): boolean {
    if (
      !this.formType.maxDurationOptions.isEnabled ||
      !this.formType.defaultDurationOptions.isEnabled ||
      this.formType.maxDurationOptions.durationType !==
        WorkflowDurationType.Time ||
      this.formType.defaultDurationOptions.durationType !==
        WorkflowDurationType.Time
    ) {
      return true
    }

    return (
      this.formType.maxDurationInMinutes >=
      this.formType.defaultDurationInMinutes
    )
  }

  public get hasUndo(): boolean {
    return this.formStateHistory.hasUndo
  }

  public get hasRedo(): boolean {
    return this.formStateHistory.hasRedo
  }

  private get isLastStepForbidden(): boolean {
    return forbiddenLastSteps.includes(this.lastWorkflowStep?.type)
  }

  private get lastWorkflowStep(): IWorkflowStep {
    return this.formType.lastStep
  }

  private setFormType = (formType: WorkflowType) => {
    runInAction(() => {
      this.formType = formType
    })

    this.onTemplateUpdate(this.formType)
  }

  private resetWorkflowStepIfNeed = () => {
    if (!this.selectedWorkflowStep?.id) {
      this.setSelectedWorkflowStepId(this.formType.initialStep.id)
    }
  }
}
